import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    fetchRoles, createRole, updateRole, deleteRole, fetchPermissionCatalog, fetchScopeVendors,
} from '../../api';
import { useAccess } from '../auth/AccessContext';
import Modal from '../marketing/Modal';
import '../reports/Reports.css';
import '../marketing/Marketing.css';
import './Users.css';

// Profiles are the permission bundles users are assigned to (Admin, Manager,
// Salesperson, Intake Agent, plus any you add). Editing one changes what
// everyone on it can see, immediately — permissions are read fresh on each
// request, so nobody has to sign out and back in.
//
// The tree of toggles comes from the server (GET /api/permissions), so a report
// added to the registry shows up here on its own.
//
// Below the toggles, a profile can be limited to specific marketing vendors.
// That's for an outside contractor who runs one vendor's campaigns: every
// report and marketing page their profile grants then shows only that vendor's
// rows (cases match through the vendor's campaign sources), and they can't
// touch — or create — any other vendor. The server applies the limit; this
// screen only sets it.

// Groups a section's children by their `group` (reports carry their catalog
// category), preserving server order.
function groupChildren(children = []) {
    const groups = [];
    const index = {};
    for (const child of children) {
        const key = child.group || '';
        if (!(key in index)) {
            index[key] = groups.length;
            groups.push({ group: key, items: [] });
        }
        groups[index[key]].items.push(child);
    }
    return groups;
}

const EMPTY_FORM = {
    name: '',
    description: '',
    permissions: new Set(),
    vendorScoped: false,
    vendorIds: new Set(),
};

export default function Profiles() {
    const { me, reload: reloadAccess } = useAccess();
    const [roles, setRoles] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // { mode: 'create' | 'edit' | 'delete', role }
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [roleList, tree, vendorList] = await Promise.all([
                fetchRoles(), fetchPermissionCatalog(), fetchScopeVendors(),
            ]);
            setRoles(roleList || []);
            setCatalog(tree || []);
            setVendors(vendorList || []);
        } catch (err) {
            setError(err.message);
            setRoles([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setDialogError(null);
        setDialog({ mode: 'create' });
    };

    const openEdit = (role) => {
        setForm({
            name: role.name,
            description: role.description || '',
            permissions: new Set(role.permissions || []),
            vendorScoped: Boolean(role.vendor_scoped),
            vendorIds: new Set((role.vendors || []).map(v => v.id)),
        });
        setDialogError(null);
        setDialog({ mode: 'edit', role });
    };

    const openDelete = (role) => {
        setDialogError(null);
        setDialog({ mode: 'delete', role });
    };

    const closeDialog = () => { setDialog(null); setDialogError(null); };

    const toggle = (key, on) => {
        setForm(f => {
            const permissions = new Set(f.permissions);
            if (on) permissions.add(key); else permissions.delete(key);
            return { ...f, permissions };
        });
    };

    // Turning a whole section on or off, including everything nested under it —
    // the common case when setting up a new profile.
    const toggleSection = (node, on) => {
        setForm(f => {
            const permissions = new Set(f.permissions);
            const keys = [node.key, ...(node.children || []).map(c => c.key)];
            for (const key of keys) {
                if (on) permissions.add(key); else permissions.delete(key);
            }
            return { ...f, permissions };
        });
    };

    const toggleVendor = (id, on) => {
        setForm(f => {
            const vendorIds = new Set(f.vendorIds);
            if (on) vendorIds.add(id); else vendorIds.delete(id);
            return { ...f, vendorIds };
        });
    };

    const handleSubmit = async () => {
        setSaving(true);
        setDialogError(null);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                permissions: [...form.permissions],
                vendor_scoped: form.vendorScoped,
                vendor_ids: form.vendorScoped ? [...form.vendorIds] : [],
            };
            if (dialog.mode === 'create') await createRole(payload);
            else if (dialog.mode === 'edit') await updateRole(dialog.role.id, payload);
            else await deleteRole(dialog.role.id);
            setDialog(null);
            await load();
            // Editing your own profile changes your own menu, so re-read it.
            if (dialog.mode === 'edit' && me && dialog.role.key === me.role_key) reloadAccess();
        } catch (err) {
            setDialogError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // The vendor limit as one line for the card, or null when unlimited.
    const summarizeScope = (role) => {
        if (role.is_admin || !role.vendor_scoped) return null;
        const names = (role.vendors || []).map(v => v.name);
        if (names.length === 0) return 'Limited to no vendors yet — sees no vendor or case data';
        return `Limited to ${names.join(', ')}`;
    };

    // A short summary of what a profile grants, for the card.
    const summarize = (role) => {
        if (role.is_admin) return 'Everything, including user management';
        const sections = catalog.filter(node => role.permissions?.includes(node.key));
        if (sections.length === 0) return 'No sections yet';
        return sections.map(section => {
            const children = section.children || [];
            if (children.length === 0) return section.label;
            const granted = children.filter(c => role.permissions.includes(c.key)).length;
            return granted === children.length
                ? `${section.label} (all ${children.length})`
                : `${section.label} (${granted} of ${children.length})`;
        }).join(' · ');
    };

    const sortedRoles = useMemo(() => roles, [roles]);

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Profiles</h1>
                    <p className="page__subtitle">
                        What each kind of user can see. Changes apply to everyone on the profile right away.
                    </p>
                </div>
                <div className="mkt__header-actions">
                    <button className="blueButton" onClick={openCreate} disabled={loading}>+ Add Profile</button>
                </div>
            </div>

            {error && <p className="mkt__error" style={{ marginTop: 16 }}>{error}</p>}
            {loading && <p className="page__subtitle">Loading profiles…</p>}

            <div className="profiles__grid">
                {sortedRoles.map(role => (
                    <div className="profiles__card" key={role.id}>
                        <div className="profiles__card-head">
                            <h2 className="profiles__name">
                                {role.name}
                                {role.is_admin && <span className="profiles__badge">Admin</span>}
                            </h2>
                            <span className="profiles__count">
                                {role.users} user{role.users === 1 ? '' : 's'}
                            </span>
                        </div>

                        {role.description && <p className="profiles__desc">{role.description}</p>}
                        <p className="profiles__summary">{summarize(role)}</p>
                        {summarizeScope(role) && (
                            <p className="profiles__scope">{summarizeScope(role)}</p>
                        )}

                        <div className="profiles__actions">
                            <button type="button" className="mkt__row-btn" onClick={() => openEdit(role)}>
                                Edit access
                            </button>
                            {!role.is_system && (
                                <button
                                    type="button"
                                    className="mkt__row-btn mkt__row-btn--danger"
                                    onClick={() => openDelete(role)}
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {dialog && dialog.mode !== 'delete' && (
                <Modal
                    title={dialog.mode === 'create' ? 'Add Profile' : `Edit ${dialog.role.name}`}
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel={dialog.mode === 'create' ? 'Add Profile' : 'Save Changes'}
                    submitting={saving}
                    submitDisabled={!form.name.trim()}
                    error={dialogError}
                >
                    <label className="mkt__field">
                        <span>Profile Name</span>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="e.g. Intake Agent"
                            autoFocus
                        />
                    </label>

                    <label className="mkt__field">
                        <span>Description (optional)</span>
                        <input
                            type="text"
                            value={form.description}
                            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="Who this profile is for"
                        />
                    </label>

                    {dialog.role?.is_admin ? (
                        <p className="mkt-modal__text">
                            The Admin profile always has access to everything, including anything added
                            later — that's what keeps user management reachable, so its permissions
                            aren't editable.
                        </p>
                    ) : (
                        <div className="perm">
                            <p className="perm__intro">Tick what this profile can open.</p>
                            {catalog.map(section => {
                                const sectionOn = form.permissions.has(section.key);
                                return (
                                    <div className="perm__section" key={section.key}>
                                        <label className="perm__section-head">
                                            <input
                                                type="checkbox"
                                                checked={sectionOn}
                                                onChange={(e) => toggleSection(section, e.target.checked)}
                                            />
                                            <span className="perm__section-label">{section.label}</span>
                                        </label>
                                        {section.description && (
                                            <p className="perm__section-desc">{section.description}</p>
                                        )}

                                        {/* Children stay visible but inert until the section itself
                                            is on — the sidebar hides a section whose pages are all
                                            off, so granting a report without Reports would do
                                            nothing and look broken. */}
                                        {(section.children || []).length > 0 && (
                                            <div className={`perm__children ${sectionOn ? '' : 'perm__children--off'}`}>
                                                {groupChildren(section.children).map(group => (
                                                    <div className="perm__group" key={group.group || 'ungrouped'}>
                                                        {group.group && <p className="perm__group-label">{group.group}</p>}
                                                        {group.items.map(child => (
                                                            <label className="perm__child" key={child.key} title={child.description}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={form.permissions.has(child.key)}
                                                                    disabled={!sectionOn}
                                                                    onChange={(e) => toggle(child.key, e.target.checked)}
                                                                />
                                                                <span>{child.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Vendor limit. Separate from the permission tree: it
                                doesn't grant anything, it narrows whatever the
                                toggles above grant to some vendors' data. */}
                            <div className="perm__section scope">
                                <label className="perm__section-head">
                                    <input
                                        type="checkbox"
                                        checked={form.vendorScoped}
                                        onChange={(e) => setForm(f => ({ ...f, vendorScoped: e.target.checked }))}
                                    />
                                    <span className="perm__section-label">Limit to specific vendors</span>
                                </label>
                                <p className="perm__section-desc">
                                    For someone who should only see one vendor's results — an outside
                                    contractor running its campaigns, say. Every report and marketing
                                    page above shows just the ticked vendors' data: their spend, their
                                    campaigns, and the cases those campaigns brought in. They can't
                                    see, edit, or add any other vendor.
                                </p>
                                {form.vendorScoped && (
                                    <div className="perm__children scope__vendors">
                                        {vendors.length === 0 && (
                                            <p className="scope__hint">
                                                No vendors exist yet. Add one on the Marketing › Vendors page first.
                                            </p>
                                        )}
                                        {vendors.map(v => (
                                            <label className="perm__child" key={v.id}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.vendorIds.has(v.id)}
                                                    onChange={(e) => toggleVendor(v.id, e.target.checked)}
                                                />
                                                <span>{v.name}</span>
                                            </label>
                                        ))}
                                        {vendors.length > 0 && form.vendorIds.size === 0 && (
                                            <p className="scope__hint scope__hint--warn">
                                                No vendor ticked: this profile will see no vendor or case
                                                data at all until one is.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {dialog && dialog.mode === 'delete' && (
                <Modal
                    title="Delete Profile"
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel="Delete Profile"
                    submitVariant="danger"
                    submitting={saving}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Delete <strong>{dialog.role.name}</strong>? This can't be undone.
                    </p>
                    {dialog.role.users > 0 && (
                        <p className="mkt-modal__text">
                            {dialog.role.users} user{dialog.role.users === 1 ? ' is' : 's are'} on this
                            profile, so it can't be deleted until they're moved to another one.
                        </p>
                    )}
                </Modal>
            )}
        </div>
    );
}
